import cn from 'classnames';
import Button from 'components/Button';
import ProductNav from 'components/Header/ProductNav';
import LinkHref from 'components/LinkHref';
import Wrapper from 'components/Wrapper';
import { Link } from 'gatsby';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import ArrowIcon from 'svgs/icons/arrow.svg';
import { loginUrl, signUpUrl } from 'utils/config';
import {
    btnLogin,
    btnSignUp,
    btnWrapper,
    header,
    content as headerContent,
    root as headerRoot,
    wrapper as headerWrapper,
    logo,
    notification as notificationC,
    notificationIsShown as notificationIsShownC,
    transparentBg as transparentBgC,
} from './Header.module.scss';
import HeaderDropdown from './HeaderDropdown';
import {
    activeLink,
    arrow,
    content,
    hasDropdown,
    link,
    links,
    navIsOpen as navIsOpenC,
    root,
    up,
    wrapper,
} from './Nav.module.scss';
import NavButton from './NavButton';
import Notification from './Notification';

const Header = ({ transparentBg, logoUrl, notification, data, productNav }) => {
  const notificationIsShown = Boolean(notification);
  const [mobileNavIsOpen, setMobileNavOpen] = useState(false);
  const [colorHeader, setHeaderColor] = useState(false);
  const transparentHeader = !colorHeader && transparentBg;

  useEffect(() => {
    const onScroll = () => setHeaderColor(window.scrollY > 200 ? true : false);

    window.removeEventListener('scroll', onScroll);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <>
      <div
        className={cn(headerRoot, {
          [notificationIsShownC]: notificationIsShown,
        })}
      >
        {notificationIsShown && (
          <Notification
            className={cn(notificationC, {
              [notificationIsShownC]: notificationIsShown,
            })}
            html={notification?.text}
          />
        )}
        <header
          className={cn(header, {
            [notificationIsShownC]: notificationIsShown,
            [transparentBgC]: transparentHeader,
          })}
        >
          <Wrapper
            Tag="nav"
            aria-labelledby="primary-navigation"
            size="L"
            className={headerWrapper}
          >
            <Link to="/" title="首頁" className={logo}>
              <img src={logoUrl.publicURL} alt="Newsleopard 電子豹" />
            </Link>
            <div className={headerContent}>
              <nav
                className={cn(root, {
                  [navIsOpenC]: mobileNavIsOpen,
                  [notificationIsShown]: notificationIsShown,
                })}
              >
                <div className={wrapper}>
                  <ul className={links}>
                    <Dropdown>
                      <Dropdown.Button>功能與服務</Dropdown.Button>
                      <Dropdown.Content>
                        <HeaderDropdown
                          notificationIsShown={notificationIsShown}
                          productData={data.product}
                        />
                      </Dropdown.Content>
                    </Dropdown>
                    <li>
                      <LinkHref
                        link="/pricing/"
                        text="價錢"
                        className={link}
                        activeClassName={activeLink}
                      />
                    </li>
                    <Dropdown>
                      <Dropdown.Button>我們的客戶</Dropdown.Button>
                      <Dropdown.Content>
                        <HeaderDropdown
                          notificationIsShown={notificationIsShown}
                          productData={data.customers}
                        />
                      </Dropdown.Content>
                    </Dropdown>
                    <Dropdown>
                      <Dropdown.Button>資源</Dropdown.Button>
                      <Dropdown.Content>
                        <HeaderDropdown
                          notificationIsShown={notificationIsShown}
                          productData={data.resource}
                        />
                      </Dropdown.Content>
                    </Dropdown>
                  </ul>
                </div>
              </nav>
              <div className={btnWrapper}>
                <Button
                  className={btnLogin}
                  href={loginUrl}
                  size="S"
                  type="ghostRegular"
                  text="登入"
                  eleId="loginBtn"
                />
                <Button
                  href={signUpUrl}
                  size="S"
                  type="primary"
                  text="免費試用"
                  className={btnSignUp}
                />
                <NavButton
                  navIsOpen={mobileNavIsOpen}
                  toggle={() => setMobileNavOpen(prev => !prev)}
                />
              </div>
            </div>
          </Wrapper>
        </header>
      </div>
      {productNav && (
        <ProductNav
          type={productNav.productName}
          isFixed={true}
          notificationIsShown={notificationIsShown}
        />
      )}
    </>
  );
};

Header.propTypes = {
  location: PropTypes.object,
  logoUrl: PropTypes.shape({
    publicURL: PropTypes.string.isRequired,
  }),
  transparentBg: PropTypes.bool,
  productNav: PropTypes.shape({
    productName: PropTypes.oneOf(['newsleopard', 'surenotify']),
  }),
  notification: PropTypes.shape({
    text: PropTypes.string.isRequired,
  }),
  data: PropTypes.object.isRequired,
};

export default Header;

const DropdownContext = React.createContext({
  open: false,
  setOpen: () => {},
});

const Dropdown = ({ children }) => {
  const [open, setOpen] = React.useState(false);
  const dropdownRef = React.useRef(null);

  React.useEffect(() => {
    function close(e) {
      if (!dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    if (open) {
      window.addEventListener('click', close);
    }
    return function removeListener() {
      window.removeEventListener('click', close);
    };
  }, [open]);

  return (
    <DropdownContext.Provider value={{ open, setOpen }}>
      <li ref={dropdownRef} className={hasDropdown}>
        {children}
      </li>
    </DropdownContext.Provider>
  );
};
Dropdown.propTypes = {
  children: PropTypes.node,
};

const DropdownButton = ({ children }) => {
  const { open, setOpen } = React.useContext(DropdownContext);
  function toggleOpen() {
    setOpen(!open);
  }

  return (
    <div onClick={toggleOpen} className={link}>
      <span>{children}</span>
      {open ? <ArrowIcon className={cn(arrow, up)} /> : <ArrowIcon className={cn(arrow)} />}
    </div>
  );
};
DropdownButton.propTypes = {
  children: PropTypes.node,
};
Dropdown.Button = DropdownButton;

const DropdownContent = ({ children }) => {
  const { open } = React.useContext(DropdownContext); // get the context

  return <div className={`${[content]} ${open ? [navIsOpenC] : ''}`}>{children}</div>;
};
DropdownContent.propTypes = {
  children: PropTypes.node,
};
Dropdown.Content = DropdownContent;
